import { useRef, type ReactNode, type Ref } from "react";
import { cn } from "@/utils/cn";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function ManagementDialog({
  open = true,
  onClose,
  title,
  description,
  widthClassName = "max-w-[960px]",
  bodyClassName,
  bodyRef,
  footer,
  isBusy = false,
  children,
}: {
  readonly open?: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly description?: string;
  readonly widthClassName?: string;
  readonly bodyClassName?: string;
  readonly bodyRef?: Ref<HTMLDivElement>;
  readonly footer?: ReactNode;
  readonly isBusy?: boolean;
  readonly children: ReactNode;
}) {
  const returnFocusRef = useRef<HTMLElement | null>(null);
  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isBusy) onClose();
      }}
    >
      <DialogContent
        overlayClassName="bg-black/45"
        hideCloseButton={isBusy}
        {...(!description ? { "aria-describedby": undefined } : {})}
        onOpenAutoFocus={() => {
          returnFocusRef.current =
            document.activeElement instanceof HTMLElement
              ? document.activeElement
              : null;
        }}
        onCloseAutoFocus={(event) => {
          if (returnFocusRef.current?.isConnected) {
            event.preventDefault();
            returnFocusRef.current.focus();
          }
        }}
        className={cn(
          "flex max-h-[85dvh] w-[calc(100vw-32px)] flex-col gap-0 overflow-hidden rounded-[14px] border-0 bg-card p-0 font-pretendard text-foreground shadow-[0_24px_80px_rgba(15,23,42,0.22)]",
          widthClassName
        )}
      >
        <DialogHeader className="shrink-0 border-b border-border px-6 py-5 text-left">
          <DialogTitle className="pr-6 text-lg font-semibold">
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="break-keep text-sm leading-5">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        <div
          ref={bodyRef}
          className={cn(
            "min-h-0 min-w-0 flex-1 overflow-auto overscroll-contain px-6 py-5",
            bodyClassName
          )}
          aria-busy={isBusy || undefined}
        >
          {children}
        </div>
        {footer && (
          <DialogFooter className="shrink-0 gap-2 border-t border-border bg-card px-6 py-4 sm:space-x-0">
            {footer}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
